<template>
  <div class="multisteps-form__panel border-radius-xl bg-white" data-animation="FadeIn">
    <h5 class="font-weight-bolder">{{ $t('departure.Animal') }}</h5>
    <p class="mb-0 text-sm">{{ $t('departure.NB_21_days_rule') }}</p>
    <div class="multisteps-form__content">
      <div class="row mt-3">
        <div class="col-12">
          <div class="mb-4">
            <p class="fw-bold text-sm">{{ $t('departure.Selected_animals') }} ({{ selectedAnimals.length }})</p>
            <div class="row">
              <div v-for="animal in selectedAnimals" :key="animal.id" class="col-md-6 mb-1">
                <div class="card border  text-sm border-success bg-light" @click="toggleSelect(animal)">                  
                  <span v-html="displayText(animal)"></span>
                  <span 
                    class="text-danger position-absolute top-1 end-0"
                    style="cursor: pointer; font-weight: bold; font-size: 25px; margin-right: 10px;"
                    @click.stop="toggleSelect(animal)"
                  >
                    ×
                  </span>
                </div>
              </div>
              <p class="text-danger text-sm" v-if="selectedAnimals.length === 0" style="margin-bottom: 2px !important;">
                {{ $t('common.No_animal_selected') }}
              </p>
              <p class="text-danger text-sm" v-if="v$.selectedAnimalIds.$error">
                {{ v$.selectedAnimalIds.$errors[0].$message }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-6">
          <!-- Search Input -->
          <div class="input-group input-group-static null">
            <label class="">{{ $t('departure.Search') }}</label>
            <input v-model="searchQuery" type="text" class="form-control form-control-default" :placeholder="$t('departure.Search_UBN_studbooknumber_animalnumber_name')"> 
          </div>
        </div>
        <div class="col-12 mt-3">
          <!-- Available animals -->
          <p class="fw-bold text-sm">{{ $t('departure.Available_animals') }}</p>
          <div class="row">
            <div v-for="animal in filteredAnimals" :key="animal.id" class="col-md-6 mb-1">
              <div class="card border text-sm" :class="{ 'border-success bg-light': isSelected(animal.id), 'cursor-pointer': true }" @click="toggleSelect(animal)">                
                <span v-html="displayText(animal)"></span>
              </div>
            </div>
          </div>
        </div>
      </div>      
      <div class="button-row d-flex mt-4">
        <button class="btn mb-0 bg-gradient-light btn-sm null null js-btn-prev" type="button" @click="validateAndPrevStep">{{ $t('common.Prev') }}</button>
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto js-btn-next" type="button" @click="validateAndNextStep">{{ $t('common.Next') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, reactive, ref, watch } from 'vue'
  import { required, helpers } from '@vuelidate/validators'
  import useVuelidate from '@vuelidate/core'
  import { useI18n } from 'vue-i18n'

  export default {
    name: "MatingFemale",
    props: {
      animals: Array,
      formData: Object,
      prevStep: Function,
      nextStep: Function
    },
    setup(props, { emit }) {
      const { t } = useI18n()      
      const localFormData = reactive(props.formData);
      
      const searchQuery = ref('')
      const selectedAnimalIds = ref(
        String(props.formData.animalIds || '').split(',').filter(id => id)
      )

      const isSelected = (id) => selectedAnimalIds.value.includes(id)

      const toggleSelect = (animal) => {
        const index = selectedAnimalIds.value.indexOf(animal.id)
        if (index > -1) {
          selectedAnimalIds.value.splice(index, 1)
        } else {
          selectedAnimalIds.value.push(animal.id)
        }
      }

      const displayText = (f) => {
        let parts = [];

        if (f.studbookNumber) parts.push(f.studbookNumber);
        if (f.animalNumber) parts.push(f.animalNumber);
        if (f.name) parts.push(`(${f.name})`);
        if (f.arrivalDate) {
          parts.push(`<span style="color: green">${f.arrivalDate}</span>`);
        }

        return parts.join(' | ');
      }
      //const displayText = (f) =>        
      //  `${f.studbookNumber ? ` ${f.studbookNumber}` : ''}${f.animalNumber ? ` | ${f.animalNumber}` : ''}${f.name ? ` (${f.name})` : ''}${f.animalNumber ? ` | ${f.animalNumber}` : ''}${f.arrivalDate ? ` | ${f.arrivalDate}` : ''}`

      const selectedAnimals = computed(() =>
        props.animals.filter(f => selectedAnimalIds.value.includes(f.id))
      )

      const filteredAnimals = computed(() =>
        props.animals.filter(f =>
          !selectedAnimalIds.value.includes(f.id) &&
          displayText(f).toLowerCase().includes(searchQuery.value.toLowerCase())
        )
      )

      // Vuelidate rules – require at least one selected animal
      const rules = computed(() => ({
        selectedAnimalIds: {
          required: helpers.withMessage(t('departure.Select_at_least_one_animal'), required),
          minOne: helpers.withMessage(t('departure.Select_at_least_one_animal'), value => value.length > 0)
        }
      }))
      
      //const v$ = useVuelidate(rules, localFormData)
      const v$ = useVuelidate(rules, { selectedAnimalIds })

      watch(localFormData, () => {
        emit('update:formData', localFormData)
      }, { deep: true })

      const validateAndPrevStep = async () => {
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          localFormData.animalIds = selectedAnimalIds.value
          props.prevStep()
        }
      }

      const validateAndNextStep = async () => {
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          localFormData.animalIds = selectedAnimalIds.value // Store in formData if needed
          props.nextStep()
        }
      }

      return {
        localFormData,
        searchQuery,
        selectedAnimalIds,
        selectedAnimals,
        filteredAnimals,
        isSelected,
        toggleSelect,
        displayText,
        v$,
        validateAndPrevStep,
        validateAndNextStep
      }
    }    
};
</script>
<style scoped>
  .card {
    transition: background-color 0.2s ease-in-out;
    cursor: pointer;
    padding: 10px !important;
  }
</style>